import React, { type FunctionComponent, useCallback, useState } from 'react'
import { type FormContextValues } from 'react-hook-form'
import styled from '@emotion/styled'
import { colors, spacing, Tooltip, RadioWithLabel } from '@retailer-platform/shared-components'
import { InformationIcon } from '@instacart/ids-core'
import { AllocationType } from 'domains/shifts/components/types'
import { useDashMessages } from 'intl/intl.hooks'
import { type PartnerPickRecurringShiftsFormData } from './recurringShift.types'
import { FormRow, StyledLabel } from './ShiftDetailsForm.styles'

interface ShiftAllocationTypeControlsProps {
  value: AllocationType
  setValue: FormContextValues<PartnerPickRecurringShiftsFormData>['setValue']
}

const Label = styled(StyledLabel)({
  marginBottom: '8px',
})

const RadioWrapper = styled.div({
  width: '100%',
  display: 'flex',
  alignItems: 'center',
})

const TooltipStyles: React.CSSProperties = {
  fill: colors.GRAYSCALE.X30,
  marginLeft: spacing.X8,
}

export const ShiftAllocationTypeControls: FunctionComponent<ShiftAllocationTypeControlsProps> = ({
  value,
  setValue,
}) => {
  const messages = useDashMessages({
    label: 'shiftsDomain.allocationType.label',
    dedicated: 'shiftsDomain.allocationType.dedicated.label',
    dedicatedTooltip: 'shiftsDomain.allocationType.dedicated.tooltip',
    oncall: 'shiftsDomain.allocationType.oncall.label',
    oncallTooltip: 'shiftsDomain.allocationType.oncall.tooltip',
  })
  const [type, setType] = useState(value)
  const handleTypeChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const type = e.target.value as AllocationType
      setValue('shiftAllocationType', type)
      setType(type)
    },
    [setValue]
  )

  return (
    <React.Fragment>
      <Label>{messages.label}</Label>

      <FormRow>
        <RadioWrapper>
          <RadioWithLabel
            required
            id="shiftAllocationTyp-dedicated"
            name="shiftAllocationType"
            label={messages.dedicated}
            value={AllocationType.Dedicated}
            checked={type === AllocationType.Dedicated}
            onChange={handleTypeChange}
          />
          <Tooltip placement="bottom" target={<InformationIcon style={TooltipStyles} />}>
            {messages.dedicatedTooltip}
          </Tooltip>
        </RadioWrapper>
        <RadioWrapper>
          <RadioWithLabel
            required
            id="shiftAllocationType-oncall"
            name="shiftAllocationType"
            label={messages.oncall}
            value={AllocationType.OnCall}
            type="radio"
            checked={type === AllocationType.OnCall}
            onChange={handleTypeChange}
          />
          <Tooltip placement="bottom" target={<InformationIcon style={TooltipStyles} />}>
            {messages.oncallTooltip}
          </Tooltip>
        </RadioWrapper>
      </FormRow>
    </React.Fragment>
  )
}
